import type { ReactNode } from 'react'

// The dark floating toolbar over a selected block (Burrow's dark brown, which
// turns cream in dark mode; 11.5px
// Manrope, 5px 9px items, the active choice lit). The 'photo' tone is the
// lighter pill Composer lab's wrap tool lays inside a photo's corner: 11px
// semibold, 4px 8px items, 86% ink, no shadow (the image toolbar).

const TONES = {
  solid: 'gap-0.5 p-1 text-[13px] font-medium shadow-[var(--shadow-chip)] rounded-lg bg-foreground',
  photo:
    'items-center gap-0.5 p-[3px] text-[11px] font-semibold rounded-lg bg-foreground/86 [&>button]:px-2 [&>button]:py-1',
} as const

export function BlockToolbar({
  label,
  align = 'left',
  tone = 'solid',
  className = '',
  children,
}: {
  label: string
  align?: 'left' | 'right'
  tone?: keyof typeof TONES
  className?: string
  children: ReactNode
}) {
  return (
    <div
      role="toolbar"
      aria-label={label}
      data-keep-selection
      onMouseDown={(e) => e.preventDefault()}
      onClick={(e) => e.stopPropagation()}
      className={`absolute z-20 flex max-w-[calc(100vw-2rem)] overflow-x-auto font-sans text-card ${TONES[tone]} ${
        className || `-top-[38px] ${align === 'right' ? 'right-0' : 'left-0'}`
      }`}
    >
      {children}
    </div>
  )
}

export function ToolbarLabel({ children }: { children: ReactNode }) {
  return <span className="whitespace-nowrap rounded-[5px] bg-card/15 px-[9px] py-[5px]">{children}</span>
}

export function ToolbarButton({
  onClick,
  accent,
  pressed,
  label,
  title,
  keys,
  children,
}: {
  onClick: () => void
  accent?: boolean
  /** A toggle in a set (image placement, scene mode), lit when on. */
  pressed?: boolean
  label?: string
  title?: string
  /** Keyboard shortcuts, for aria-keyshortcuts. */
  keys?: string
  children: ReactNode
}) {
  return (
    <button
      type="button"
      aria-pressed={pressed}
      aria-label={label}
      aria-keyshortcuts={keys}
      title={title}
      onClick={(e) => {
        e.stopPropagation()
        onClick()
      }}
      className={`whitespace-nowrap rounded-[5px] px-[9px] py-[5px] ${pressed ? 'bg-card/15' : 'hover:bg-card/10'} ${accent ? 'text-accent' : ''}`}
    >
      {children}
    </button>
  )
}

/** Remove: just the word, no ⌫ glyph beside it (Niki, 10/2). A selected card still goes with Backspace or Delete. */
export function RemoveButton({ onClick }: { onClick: () => void }) {
  return (
    <ToolbarButton onClick={onClick} keys="Backspace Delete" title="Remove (Backspace)">
      Remove
    </ToolbarButton>
  )
}

export function ToolbarDivider() {
  return <span aria-hidden className="mx-0.5 w-px self-stretch bg-card/20" />
}
